const BASE = process.env.NEXT_PUBLIC_API_URL ?? "https://api2.astrology.company";
const SITE_ID = process.env.NEXT_PUBLIC_SITE_ID ?? "tsr";

let accessToken: string | null = null;
let refreshTokenValue: string | null = null;

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshTokenValue = refresh;
  if (typeof window !== "undefined") {
    localStorage.setItem("tsr_refresh_token", refresh);
  }
}

export function clearTokens() {
  accessToken = null;
  refreshTokenValue = null;
  if (typeof window !== "undefined") {
    localStorage.removeItem("tsr_refresh_token");
  }
}

export function getStoredRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("tsr_refresh_token");
}

async function refreshAccessToken(): Promise<boolean> {
  const rt = refreshTokenValue ?? getStoredRefreshToken();
  if (!rt) return false;
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });
  if (!res.ok) {
    clearTokens();
    return false;
  }
  const data = await res.json();
  accessToken = data.accessToken;
  return true;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401 && retry) {
    const ok = await refreshAccessToken();
    if (ok) return request<T>(path, options, false);
    clearTokens();
    if (typeof window !== "undefined") {
      window.location.href = "/account/login";
    }
    throw new ApiError(401, "Session expired", null);
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json();
  if (!res.ok) throw new ApiError(res.status, body.error ?? "Request failed", body);
  return body as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body: unknown
  ) {
    super(message);
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  siteId: string;
  emailVerified: boolean;
  avatarUrl: string | null;
  providers: string[];
  creditBalance?: number;
  createdAt?: string;
}

export interface AuthResult {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export interface Chart {
  chartId: string;
  julianDayUt: number;
  birthData: {
    name: string | null;
    gender: string | null;
    dateOfBirth: string;
    timeOfBirth: string | null;
    timeUnknown: boolean;
    latitude: number;
    longitude: number;
    town: string | null;
    country: string | null;
    houseSystem: string;
    siteId: string;
  };
  planets: ChartPlanet[];
  houses: ChartHouse[];
  aspects: ChartAspect[];
}

export interface ChartPlanet {
  planet: string;
  longitude: number;
  sign: string;
  signNumber: number;
  houseNumber: number | null;
  isRetrograde: boolean;
  degMinSec: string | null;
}

export interface ChartHouse {
  houseNumber: number;
  cuspLongitude: number;
  sign: string;
}

export interface ChartAspect {
  planetA: string;
  planetB: string;
  aspect: string;
  orb: number;
  applying: boolean;
}

export interface CreateChartInput {
  name?: string;
  gender?: string;
  dateOfBirth: string; // YYYY-MM-DD
  timeOfBirth?: string; // HH:MM
  timeUnknown?: boolean;
  latitude: number;
  longitude: number;
  town?: string;
  country?: string;
  houseSystem?: string;
}

export interface Report {
  reportId: string;
  chartId: string;
  reportType: string;
  tier: string;
  astrologer: { id: string; displayName: string };
  contentHtml: string;
  chartUrl?: string;
  sectionCount: number;
  interpretationCount: number;
  generatedAt: string;
  fromCache: boolean;
}

export interface CreateReportInput {
  chartId: number;
  reportType: string;
  tier: string;
  astrologerId?: number;
  returnYear?: number;
  returnLatitude?: number;
  returnLongitude?: number;
  progressionYear?: number;
  includeIntro?: boolean;
  includeOutro?: boolean;
  includeImages?: boolean;
  includeQuotes?: boolean;
}

export interface CreateCompatibilityInput {
  chartIdA: number;
  chartIdB: number;
  reportType: string;
  tier: string;
  astrologerId?: number;
  includeIntro?: boolean;
  includeOutro?: boolean;
  includeImages?: boolean;
  includeQuotes?: boolean;
}

export interface CompatibilityReport {
  reportId: string;
  chartIdA: string;
  chartIdB: string;
  reportType: string;
  tier: string;
  astrologer: { id: string; displayName: string };
  contentHtml: string;
  chartUrl?: string;
  sectionCount: number;
  interpretationCount: number;
  generatedAt: string;
  fromCache: boolean;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const auth = {
  register: (email: string, password: string, name?: string) =>
    request<AuthResult>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name, siteId: SITE_ID }),
    }),

  login: (email: string, password: string) =>
    request<AuthResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, siteId: SITE_ID }),
    }),

  logout: (rt: string) =>
    request<void>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken: rt }),
    }),

  refresh: (rt: string) =>
    fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: rt }),
    }).then((r) => (r.ok ? r.json() : null)) as Promise<{ accessToken: string } | null>,

  loginWithGoogle: (providerToken: string) =>
    request<AuthResult>("/auth/oauth/google/token", {
      method: "POST",
      body: JSON.stringify({ providerToken, siteId: SITE_ID }),
    }),

  verifyEmail: (token: string) =>
    request<{ message: string }>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  resendVerification: (email: string) =>
    request<{ message: string }>("/auth/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email, siteId: SITE_ID }),
    }),

  forgotPassword: (email: string) =>
    request<{ message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email, siteId: SITE_ID }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ message: string }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
};

// ── Charts ────────────────────────────────────────────────────────────────────

// Chart/report creation goes via Next.js API proxy routes so the API key
// stays server-side and is never exposed to the browser.
async function proxyPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new ApiError(res.status, data.error ?? "Request failed", data);
  return data as T;
}

async function proxyGet<T>(path: string): Promise<T> {
  const res = await fetch(path);
  const data = await res.json();
  if (!res.ok) throw new ApiError(res.status, data.error ?? "Request failed", data);
  return data as T;
}

export const charts = {
  create: (data: CreateChartInput) =>
    proxyPost<Chart>("/api/charts", { ...data, siteId: SITE_ID }),

  get: (id: string) => request<Chart>(`/charts/${id}`),
};

// ── User charts (owned) ───────────────────────────────────────────────────────

export interface UserChartListItem {
  id: string;
  name: string | null;
  gender: string | null;
  dateOfBirth: string;
  timeOfBirth: string | null;
  timeUnknown: boolean;
  town: string | null;
  country: string | null;
  createdAt: string;
}

export const userCharts = {
  list: () => request<UserChartListItem[]>("/users/me/charts"),

  create: (data: CreateChartInput) =>
    request<Chart>("/users/me/charts", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  get: (id: string) => request<Chart>(`/users/me/charts/${id}`),

  delete: (id: string) =>
    request<void>(`/users/me/charts/${id}`, { method: "DELETE" }),

  update: (id: string, data: CreateChartInput) =>
    request<Chart>(`/users/me/charts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// ── Reports ───────────────────────────────────────────────────────────────────

export const reports = {
  create: (data: CreateReportInput) =>
    proxyPost<Report>("/api/reports", data),

  get: (id: string) => proxyGet<Report>(`/api/reports/${id}`),
};

// ── Compatibility ─────────────────────────────────────────────────────────────

export const compatibility = {
  create: (data: CreateCompatibilityInput) =>
    proxyPost<CompatibilityReport>("/api/compatibility", data),
};;

// ── Lookup ────────────────────────────────────────────────────────────────────

export interface ReportType {
  id: number;
  code: string;
  label: string;
}

export const lookup = {
  reportTypes: () => request<ReportType[]>("/lookup/report-types"),
};

// ── Credits ────────────────────────────────────────────────────────────────────

export interface CreditBalance {
  balance: number;
  transactions: CreditTransaction[];
}

export interface CreditTransaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  reference: string | null;
  createdAt: string;
}

export const credits = {
  get: () => request<CreditBalance>("/users/me/credits"),

  purchase: (amount: number, paymentReference?: string) =>
    request<{ balance: number }>("/users/me/credits/purchase", {
      method: "POST",
      body: JSON.stringify({ amount, paymentReference }),
    }),
};

// ── Preferences ────────────────────────────────────────────────────────────────

export interface UserPreferences {
  preferences: Record<string, unknown>;
}

export const preferences = {
  get: () => request<UserPreferences>("/users/me/preferences"),

  update: (prefs: Record<string, unknown>) =>
    request<UserPreferences>("/users/me/preferences", {
      method: "PUT",
      body: JSON.stringify({ preferences: prefs }),
    }),
};

// ── User profile ───────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: string;
  siteId: string;
  emailVerified: boolean;
  avatarUrl: string | null;
  providers: string[];
  creditBalance: number;
  createdAt: string;
}

export const profile = {
  get: () => request<UserProfile>("/users/me"),

  update: (data: { name?: string }) =>
    request<UserProfile>("/users/me", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// ── Password change ────────────────────────────────────────────────────────────

export const password = {
  change: (currentPassword: string, newPassword: string) =>
    request<void>("/auth/password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};
